import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleChange, MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { BreadcrumbService } from '../../../domain/services/breadcrumb/breadcrumb.service';
import { DialogService } from '../../../domain/services/dialog/dialog.service';
import { GatewayService } from '../../../domain/services/gateway/gateway.service';
import { GatewayConfig } from '../../../domain/models/gateway.model';
import { GatewayLogViewerComponent } from './gateway-log-viewer.component';

@Component({
  selector: 'app-payment-processor',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCardModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSnackBarModule,
    MatTabsModule,
    GatewayLogViewerComponent,
  ],
  templateUrl: './payment-processor.component.html',
  styleUrl: './payment-processor.component.css',
})
export class PaymentProcessorComponent implements OnInit {
  configs: GatewayConfig[] = [];
  forms: Record<number, FormGroup> = {};
  loadError: string | null = null;

  /** The processor actually taking payments right now — drives the ACTIVE badge and the live-now strip. */
  activeConfigId: number | null = null;

  /**
   * Which config's credentials panel is on screen. Pure navigation — the
   * toggle is bound to this, not to activeConfigId, so browsing an
   * environment never touches live traffic. Going live is a separate,
   * explicit action (onSwitchLive).
   */
  selectedConfigId: number | null = null;

  constructor(
    private readonly fb: FormBuilder,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly dialogService: DialogService,
    private readonly gatewayService: GatewayService,
    private readonly snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.breadcrumbService.updateBreadcrumbs([
      { icon: 'account_balance', url: '/app/admin/payment-processor', label: 'Administrative' },
      { icon: 'credit_card', url: '/app/admin/payment-processor', label: 'Payment Processor' },
    ]);
    this.loadConfigs();
  }

  get liveConfig(): GatewayConfig | undefined {
    return this.configs.find((c) => c.id === this.activeConfigId);
  }

  get selectedConfig(): GatewayConfig | undefined {
    return this.configs.find((c) => c.id === this.selectedConfigId);
  }

  /** Toggle-button label, and what the confirm dialog + switch button call the target. */
  environmentLabel(config: GatewayConfig): string {
    if (config.processor === 'MOCK') return 'Mock';
    return `Catalis ${this.titleCase(config.environment)}`;
  }

  /** Toggle-button display text — marks whichever one is actually live, without controlling anything. */
  toggleLabel(config: GatewayConfig): string {
    return config.id === this.activeConfigId
      ? `${this.environmentLabel(config)} • Live`
      : this.environmentLabel(config);
  }

  liveStatusTitle(config: GatewayConfig): string {
    if (config.processor === 'MOCK') return 'Mock — no real charges';
    return `Catalis — ${this.titleCase(config.environment)}`;
  }

  liveStatusSubtitle(config: GatewayConfig): string {
    return this.moneyMessage(config, false);
  }

  /** True only for Catalis Prod — the one case where real money actually moves. Drives the danger dot. */
  isLiveMoney(config: GatewayConfig): boolean {
    return config.processor === 'CATALIS' && config.environment === 'PROD';
  }

  /**
   * Real-money warning text, by processor + environment. Stage is Catalis's sandbox —
   * it must never be lumped in with Prod's "charged for real" warning.
   */
  private moneyMessage(config: GatewayConfig, future: boolean): string {
    if (config.processor === 'MOCK') {
      return future ? 'Payments will be simulated. No money moves.' : 'Payments are simulated. No money moves.';
    }
    if (config.environment === 'PROD') {
      return future ? 'Card payments will be charged for real.' : 'Card payments are charged for real.';
    }
    return 'Catalis test environment. Cards are authorized against the sandbox — no real money moves.';
  }

  private titleCase(s: string): string {
    return s.length ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s;
  }

  loadConfigs(): void {
    this.gatewayService.getConfigs().subscribe({
      next: (configs) => {
        this.loadError = null;
        this.configs = configs;
        this.activeConfigId = configs.find((c) => c.active)?.id ?? null;
        // Preserve whatever the admin is currently viewing across a save/switch
        // reload; only fall back to the live row if that selection is gone.
        if (!configs.some((c) => c.id === this.selectedConfigId)) {
          this.selectedConfigId = this.activeConfigId;
        }
        this.forms = {};
        configs
          .filter((c) => c.processor === 'CATALIS')
          .forEach((c) => {
            this.forms[c.id] = this.fb.group({
              baseUrl: [c.baseUrl],
              authUrl: [c.authUrl],
              clientId: [c.clientId],
              clientSecret: [''],
              merchantId: [c.merchantId],
            });
          });
      },
      error: (err) => {
        const status = err?.status ?? 0;
        this.loadError = status === 403
          ? 'This account needs the Administrative module to view payment processor settings.'
          : `Couldn't load payment processor settings (HTTP ${status}): `
            + (err?.error?.message || err?.message || 'Unable to reach the server.');
        this.configs = [];
        this.forms = {};
      },
    });
  }

  secretPlaceholder(config: GatewayConfig): string {
    return config.secretSet ? '••••••••' : '';
  }

  onSave(config: GatewayConfig): void {
    const form = this.forms[config.id];
    if (!form) return;
    const v = form.value;
    const payload: GatewayConfig = {
      ...config,
      baseUrl: v.baseUrl,
      authUrl: v.authUrl,
      clientId: v.clientId,
      merchantId: v.merchantId,
    };
    // Blank clientSecret means "keep current" — leave it null so we don't overwrite the stored value.
    if (v.clientSecret) {
      payload.clientSecret = v.clientSecret;
    }
    this.gatewayService.updateConfig(payload).subscribe({
      next: () => {
        this.snackBar.open('Changes saved.', 'Dismiss', { duration: 3000 });
        this.loadConfigs();
      },
      error: (err) => {
        this.snackBar.open(err?.error?.message || "Couldn't save changes.", 'Dismiss', { duration: 5000 });
      },
    });
  }

  onTest(config: GatewayConfig): void {
    this.gatewayService.testConfig(config.id).subscribe({
      next: (res) => {
        this.snackBar.open(res?.result || 'Connection test succeeded.', 'Dismiss', { duration: 4000 });
      },
      error: (err) => {
        this.snackBar.open(err?.error?.message || 'Connection test failed.', 'Dismiss', { duration: 5000 });
      },
    });
  }

  /** Pure navigation: pick which panel to look at. No dialog, no activation, no side effects. */
  onSelectEnvironment(event: MatButtonToggleChange): void {
    this.selectedConfigId = event.value;
  }

  /** The explicit "go live" action — separate from browsing. Nothing to revert on failure/cancel. */
  onSwitchLive(config: GatewayConfig): void {
    const label = this.environmentLabel(config);
    this.dialogService.openConfirmationDialog(
      `Switch to ${label}`,
      this.moneyMessage(config, true),
      (confirmed: boolean) => {
        if (!confirmed) return;
        this.gatewayService.activateConfig(config.id).subscribe({
          next: () => {
            this.snackBar.open(`${label} is now live.`, 'Dismiss', { duration: 3000 });
            this.loadConfigs();
          },
          error: (err) => {
            this.snackBar.open(err?.error?.message || `Couldn't switch to ${label}.`, 'Dismiss', { duration: 5000 });
          },
        });
      },
    );
  }
}
